import { z } from 'zod';

/**
 * Email validation shared by every app.
 *
 * This exists because the same rule was hand-rolled in four different places and
 * drifted. The worst copy was `/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i`, which
 * capped the TLD at four characters and so rejected valid addresses on any longer
 * TLD - `.global`, `.online`, `.digital`, `.technology`, `.pickleball` - while still
 * accepting `a..b@example.com`, `.a@example.com` and `a@example..com`.
 *
 * The format check is zod's, not ours: it is maintained upstream and is already what
 * the server actions validate with. What is layered on top is the product policy the
 * apps have to agree on - trimming, and the length limits.
 */

/**
 * Product cap on the whole address. RFC 5321 allows 254, but every email input in
 * the apps is `maxLength={100}` and the API column matches, so 100 is the real limit.
 */
export const EMAIL_MAX_LENGTH = 100;

/** RFC 5321 caps the local part (everything before the `@`) at 64 characters. */
const EMAIL_MAX_LOCAL_PART_LENGTH = 64;

/**
 * Leading/trailing whitespace is the most common paste error. Strip it instead of
 * failing the user for something we can fix ourselves.
 */
export const normalizeEmail = (email: string | undefined | null): string => (email ?? '').trim();

interface EmailSchemaMessages {
  required?: string;
  invalid?: string;
  tooLong?: string;
}

/**
 * Checks run in order, and zodResolver surfaces the first failure - so an empty field
 * reads "required" rather than "invalid".
 */
export const createEmailSchema = ({
  required = 'Email is required.',
  invalid = 'Invalid email address.',
  tooLong = `Email must not exceed ${EMAIL_MAX_LENGTH} characters.`,
}: EmailSchemaMessages = {}) =>
  z
    .string()
    .trim()
    .min(1, required)
    .email(invalid)
    .max(EMAIL_MAX_LENGTH, tooLong)
    .refine((value) => (value.split('@')[0] ?? '').length <= EMAIL_MAX_LOCAL_PART_LENGTH, invalid);

export const emailSchema = createEmailSchema();

export const isValidEmail = (email: string | undefined | null): boolean =>
  emailSchema.safeParse(email).success;

interface EmailFieldRulesOptions {
  requiredMessage?: string;
  invalidMessage?: string;
  tooLongMessage?: string;
  /** Admins and editors are allowed to save addresses that fail the format check. */
  allowAnyFormat?: boolean;
  /** Blank is accepted - for fields like a billing email that falls back to another. */
  optional?: boolean;
}

/**
 * react-hook-form rules for an email input, for the forms that validate per field
 * instead of through a zod resolver. Same definition of "valid" either way.
 */
export const getEmailFieldRules = ({
  requiredMessage = 'Email is required.',
  invalidMessage = 'Invalid email address.',
  tooLongMessage = `Email must not exceed ${EMAIL_MAX_LENGTH} characters.`,
  allowAnyFormat = false,
  optional = false,
}: EmailFieldRulesOptions = {}) => ({
  ...(optional ? {} : { required: requiredMessage }),
  // Inputs already carry maxLength, but a legacy value longer than the cap can still
  // arrive from the API - this reports the length rather than a vague "invalid".
  maxLength: { value: EMAIL_MAX_LENGTH, message: tooLongMessage },
  setValueAs: normalizeEmail,
  ...(allowAnyFormat
    ? {}
    : {
        // Widest input type on purpose: an optional field registers as
        // `string | undefined`, and a narrower parameter is not assignable there.
        validate: (value: string | undefined | null) =>
          (optional && !normalizeEmail(value)) || isValidEmail(value) || invalidMessage,
      }),
});
